// Date and Time Formatters
export const formatDate = (date: string | Date, options?: Intl.DateTimeFormatOptions): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  const defaultOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  };
  
  return new Intl.DateTimeFormat('tr-TR', { ...defaultOptions, ...options }).format(dateObj);
};

export const formatDateTime = (date: string | Date): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  return new Intl.DateTimeFormat('tr-TR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(dateObj);
};

export const formatTime = (date: string | Date): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  return new Intl.DateTimeFormat('tr-TR', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(dateObj);
};

export const formatRelativeTime = (date: string | Date): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - dateObj.getTime()) / 1000);
  
  if (diffInSeconds < 60) {
    return 'Az önce';
  } else if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return `${minutes} dakika önce`;
  } else if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return `${hours} saat önce`;
  } else if (diffInSeconds < 2592000) {
    const days = Math.floor(diffInSeconds / 86400);
    return `${days} gün önce`;
  } else {
    return formatDate(dateObj);
  }
};

// Currency Formatters
export const formatCurrency = (amount: number, currency: string = 'TRY'): string => {
  return new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

export const formatCurrencyCompact = (amount: number, currency: string = 'TRY'): string => {
  return new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency,
    notation: 'compact',
    compactDisplay: 'short',
  }).format(amount);
};

// Number Formatters
export const formatNumber = (num: number, decimals: number = 2): string => {
  return new Intl.NumberFormat('tr-TR', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(num);
};

export const formatNumberCompact = (num: number): string => {
  return new Intl.NumberFormat('tr-TR', {
    notation: 'compact',
    compactDisplay: 'short',
  }).format(num);
};

export const formatPercentage = (value: number, decimals: number = 1): string => {
  return new Intl.NumberFormat('tr-TR', {
    style: 'percent',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value / 100);
};

// Text Formatters
export const capitalizeFirst = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export const formatPhoneNumber = (phone: string): string => {
  // Remove all non-digit characters
  const cleaned = phone.replace(/\D/g, '');
  
  // Format Turkish phone numbers
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)} ${cleaned.slice(6, 8)} ${cleaned.slice(8)}`;
  } else if (cleaned.length === 11 && cleaned.startsWith('0')) {
    return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)} ${cleaned.slice(7, 9)} ${cleaned.slice(9)}`;
  }
  
  return phone;
};

export const formatOrderNumber = (orderNumber: string): string => {
  // Add spaces every 4 characters for better readability
  return orderNumber.replace(/(.{4})/g, '$1 ').trim();
};

export const formatProductCode = (productCode: string): string => {
  // Format product codes with proper spacing
  return productCode.replace(/([A-Z]{3})(\d{3})/, '$1-$2');
};

// Status Formatters
export const formatOrderStatus = (status: string): string => {
  const statusMap: Record<string, string> = {
    pending_approval: 'Onay Bekliyor',
    approved: 'Onaylandı',
    rejected: 'Reddedildi',
    preparing: 'Hazırlanıyor',
    ready_for_shipping: 'Kargoya Hazır',
    shipped: 'Kargolandı',
    in_transit: 'Yolda',
    out_for_delivery: 'Teslimat İçin Çıktı',
    delivered: 'Teslim Edildi',
    cancelled: 'İptal Edildi',
    returned: 'İade Edildi',
    refunded: 'İade Ödendi',
  };
  
  return statusMap[status] || status;
};

export const formatStockStatus = (status: string): string => {
  const statusMap: Record<string, string> = {
    in_stock: 'Stokta',
    low_stock: 'Düşük Stok',
    out_of_stock: 'Stokta Yok',
  };
  
  return statusMap[status] || status;
};

export const formatUserRole = (role: string): string => {
  const roleMap: Record<string, string> = {
    admin: 'Yönetici',
    manager: 'Müdür',
    salesperson: 'Satış Temsilcisi',
    warehouse: 'Depo Sorumlusu',
    driver: 'Sürücü',
  };
  
  return roleMap[role] || role;
};

// File Size Formatters
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Address Formatters
export const formatAddress = (address: string): string => {
  // Clean up address formatting
  return address
    .replace(/\s+/g, ' ')
    .replace(/,/g, ', ')
    .trim();
};

// Credit Card Formatters
export const formatCreditCard = (cardNumber: string): string => {
  const cleaned = cardNumber.replace(/\D/g, '');
  const match = cleaned.match(/(\d{4})(\d{4})(\d{4})(\d{4})/);
  
  if (match) {
    return `${match[1]} ${match[2]} ${match[3]} ${match[4]}`;
  }
  
  return cardNumber;
};

export const maskCreditCard = (cardNumber: string): string => {
  const cleaned = cardNumber.replace(/\D/g, '');
  
  if (cleaned.length >= 4) {
    const lastFour = cleaned.slice(-4);
    const masked = '*'.repeat(cleaned.length - 4);
    return `${masked}${lastFour}`;
  }
  
  return cardNumber;
};

// Validation Formatters
export const formatValidationError = (field: string, error: string): string => {
  const fieldMap: Record<string, string> = {
    email: 'E-posta',
    password: 'Şifre',
    username: 'Kullanıcı adı',
    full_name: 'Ad soyad',
    phone: 'Telefon',
    address: 'Adres',
    customer_name: 'Müşteri adı',
    customer_email: 'Müşteri e-postası',
    customer_phone: 'Müşteri telefonu',
    total_amount: 'Toplam tutar',
    quantity: 'Miktar',
    unit_price: 'Birim fiyat',
  };
  
  const fieldName = fieldMap[field] || field;
  return `${fieldName}: ${error}`;
};

// Export all formatters
const formatters = {
  // Date and Time
  formatDate,
  formatDateTime,
  formatTime,
  formatRelativeTime,
  
  // Currency
  formatCurrency,
  formatCurrencyCompact,
  
  // Numbers
  formatNumber,
  formatNumberCompact,
  formatPercentage,
  
  // Text
  capitalizeFirst,
  formatPhoneNumber,
  formatOrderNumber,
  formatProductCode,
  
  // Status
  formatOrderStatus,
  formatStockStatus,
  formatUserRole,
  
  // File
  formatFileSize,
  
  // Address
  formatAddress,
  
  // Credit Card
  formatCreditCard,
  maskCreditCard,
  
  // Validation
  formatValidationError,
};

export default formatters; 